import { Alert, Button, Text, Card, CardColors, Image } from '@/ui'
import {
  createUpriseAccount,
  FinancialAdvisoryProfile,
  FinancialAdvisoryProfilesStatus,
  putFinancialAdvisoryProfile,
} from '../financialAdvisory.slice'
import { useState } from 'react'
import { track } from '@amplitude/analytics-browser'
import { AdvisorHelpSummaryCard } from './PreIntroCallPanel'
import { useAppDispatch } from '@/utils/typeHelpers'
import { TypographyStyles } from '@/styles'
import { CaseStyle, useTherapistFlag } from '@/utils/launchdarklyHelpers'

export const NotInterestedPanel = ({
  faProfile,
}: {
  faProfile: FinancialAdvisoryProfile
}) => {
  const dispatch = useAppDispatch()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const replacementText = useTherapistFlag(CaseStyle.lowerCase, true)
  // This officially enables the user to start onboarding to Uprise
  const submitUpriseOnboarding = async () => {
    setLoading(true)
    setError('')
    try {
      track('started uprise onboarding — post cancellation or disinterest', {
        fa_profile_id: faProfile.id,
        user_id: faProfile.userId,
      })
      const response = await createUpriseAccount()(dispatch)
      await putFinancialAdvisoryProfile(faProfile.id, {
        status: FinancialAdvisoryProfilesStatus.onboarding,
      })(dispatch)
      if (!response) {
        setError('Something went wrong! Please refresh and try again.')
      }
    } catch {
      setError('An unexpected error occurred. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {error && (
        <Alert
          variant="error"
          description="It looks like we encountered an error while trying to onboard you! Please try again."
        />
      )}

      <Card color={CardColors.LEVEL_1}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="w-full lg:w-5/12 flex justify-center">
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/two_chairs.svg"
                alt="heard financial advisory"
                width="300"
              />
            </div>
            <div className="w-full lg:w-7/12 space-y-4">
              <Text variant={TypographyStyles.H2}>
                Thank you for trying Financial Advisory!
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                As a reminder, Heard has partnered with Uprise, a premium
                financial advisory platform, to connect our {replacementText}{' '}
                with financial experts geared towards {replacementText} small
                business owners like you.
              </Text>
              <Text variant={TypographyStyles.BODY_MD}>
                You previously indicated that you were no longer interested in
                the financial advisory experience.
              </Text>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <div className="w-full max-w-sm">
              <Button
                variant="primary"
                className="w-full"
                loading={loading}
                disabled={loading}
                onClick={submitUpriseOnboarding}
              >
                Start Onboarding
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div className="my-6">
        <AdvisorHelpSummaryCard />
      </div>
    </>
  )
}
